import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import {
  deleteIntercompanyPair,
  getIntercompanyCapabilities,
  listIntercompanyPairs,
  reconcileIntercompany,
} from "@/api/intercompany";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatRuDateTime } from "@/lib/format";
import { SELECT_CLASSES } from "@/lib/styles";
import { PairFormDialog } from "@/pages/intercompany/PairFormDialog";
import { ReconTable } from "@/pages/intercompany/ReconTable";
import type { IntercompanyCapabilities, IntercompanyPairView } from "@/types/api";

const READ_ONLY_CAPS: IntercompanyCapabilities = { can_create_sf: false, can_mark_delete: false };

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function lastMonthRange(): { from: string; to: string } {
  const now = new Date();
  const firstThis = new Date(now.getFullYear(), now.getMonth(), 1);
  const firstPrev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastPrev = new Date(firstThis.getTime() - 86_400_000);
  return { from: isoDate(firstPrev), to: isoDate(lastPrev) };
}
function nextDayIso(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return isoDate(d);
}

export function IntercompanyPage(): React.ReactElement {
  const qc = useQueryClient();

  // Пары — per-user (не зависят от активного подключения): запрос не гейтим на connId.
  const pairsQ = useQuery({
    queryKey: ["intercompany-pairs"],
    queryFn: listIntercompanyPairs,
  });
  const pairs = useMemo(() => pairsQ.data ?? [], [pairsQ.data]);

  const capsQ = useQuery({
    queryKey: ["intercompany-capabilities"],
    queryFn: getIntercompanyCapabilities,
    staleTime: 5 * 60_000,
  });
  // По умолчанию (пока грузится / при ошибке) — режим только чтения.
  const caps = capsQ.data ?? READ_ONLY_CAPS;
  const readOnly = !caps.can_create_sf && !caps.can_mark_delete;

  const [pairId, setPairId] = useState<string>("");
  const [showForm, setShowForm] = useState(false);
  const [editingPair, setEditingPair] = useState<IntercompanyPairView | null>(null);
  const defaults = useMemo(lastMonthRange, []);
  const [from, setFrom] = useState(defaults.from);
  const [to, setTo] = useState(defaults.to);

  const reconcileMut = useMutation({
    mutationFn: () =>
      reconcileIntercompany(pairId, { dateFrom: from, dateTo: nextDayIso(to) }),
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail ?? String(err);
      toast.error("Сверка не выполнена", { description: String(detail) });
    },
  });
  const result = reconcileMut.data;

  useEffect(() => {
    if (pairs.length === 1 && !pairId) setPairId(pairs[0]!.id);
    if (pairId && pairs.length > 0 && !pairs.some((p) => p.id === pairId)) {
      setPairId("");
      reconcileMut.reset();
    }
  }, [pairs, pairId, reconcileMut]);

  const selectedPair = pairs.find((p) => p.id === pairId) ?? null;

  const deleteMut = useMutation({
    mutationFn: deleteIntercompanyPair,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["intercompany-pairs"] });
      reconcileMut.reset();
      setPairId("");
      toast.success("Пара удалена");
    },
    onError: (err) => toast.error("Не удалось удалить пару", { description: String(err) }),
  });

  const canRun = !!pairId && !!from && !!to && from <= to && !reconcileMut.isPending;

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Сверка интеркампани</h1>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Сверяет передачи товаров между организациями (УНФ) с поступлениями и
            счёт-фактурами полученными в двух БП. Позволяет точечно создать недостающие
            СФ полученные в БП получателя и пометить на удаление лишние в БП передатчика.
            Результаты сверки нигде не сохраняются — при перезаходе пересчитываются заново.
          </p>
        </div>
        <Button onClick={() => { setEditingPair(null); setShowForm(true); }}>
          <Plus className="mr-2 h-4 w-4" />
          Новая пара
        </Button>
      </header>

      {readOnly && (
        <div className="flex items-center gap-2 rounded-md border border-slate-300 bg-slate-50 p-3 text-sm text-slate-700">
          <Lock className="h-4 w-4 shrink-0" />
          Режим просмотра: создание СФ и пометка на удаление отключены на сервере
          (feature-flag'и <code>ALLOW_INTERCOMPANY_CREATE_SF</code> / <code>ALLOW_INTERCOMPANY_MARK_DELETE</code>).
          «Запустить сверку» только читает данные из 1С — ничего не меняется.
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 rounded-md border p-4">
        <div className="space-y-1.5">
          <Label htmlFor="pair">Пара организаций</Label>
          <select
            id="pair"
            className={SELECT_CLASSES}
            value={pairId}
            onChange={(e) => { setPairId(e.target.value); reconcileMut.reset(); }}
            disabled={pairsQ.isLoading}
          >
            <option value="">{pairsQ.isLoading ? "Загрузка…" : "— выберите пару —"}</option>
            {pairs.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} (ИНН {p.sender_inn} → {p.receiver_inn})
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="from">Период с</Label>
          <Input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="to">по (включительно)</Label>
          <Input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" />
        </div>
        <Button disabled={!canRun} onClick={() => reconcileMut.mutate()}>
          <RefreshCw className={`mr-2 h-4 w-4 ${reconcileMut.isPending ? "animate-spin" : ""}`} />
          {reconcileMut.isPending ? "Сверка…" : "Запустить сверку"}
        </Button>
        {selectedPair && (
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={() => { setEditingPair(selectedPair); setShowForm(true); }}>
              <Pencil className="mr-1 h-3.5 w-3.5" />Изменить
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (window.confirm(`Удалить пару «${selectedPair.name}»? Это только настройка, документы в 1С не трогаются.`)) {
                  deleteMut.mutate(selectedPair.id);
                }
              }}
            >
              <Trash2 className="h-4 w-4 text-rose-500" />
            </Button>
          </div>
        )}
      </div>

      {pairsQ.data && pairs.length === 0 && (
        <div className="rounded-md border border-dashed p-10 text-center text-sm text-muted-foreground">
          Пар пока нет. Нажмите «Новая пара» и укажите ИНН передатчика/получателя и три подключения к 1С.
        </div>
      )}

      {reconcileMut.isError && !result && (
        <p className="text-sm text-rose-600">Ошибка сверки — см. уведомление выше.</p>
      )}

      {result && (
        <>
          <p className="text-xs text-muted-foreground">
            Сверка от {formatRuDateTime(result.generated_at)} · период{" "}
            {result.date_from} … {result.date_to} (исключая)
          </p>
          {/* key={result.generated_at} — каждый новый запуск сверки = свежий маунт таблицы,
              чтобы постатусные пометки строк (statuses, selected, pending) не «прилипали»
              к чужим строкам после изменения набора строк (напр. помеченные на удаление СФ
              уходят из списка → индексы сдвигаются, статус «помечен» цеплялся к соседу). */}
          <ReconTable key={result.generated_at} result={result} pairId={pairId} caps={caps} />
        </>
      )}

      {showForm && (
        <PairFormDialog pair={editingPair} onClose={() => setShowForm(false)} />
      )}
    </div>
  );
}
